'use client';

import { useState } from 'react';

import { Logo } from '@/components/logo';
import { SearchIconButton } from '@/components/search-icon-button';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { NAV_ITEMS } from '@/config/navigation-items';
import { useScrollNavigation } from '@/hooks/use-scroll-navigation';
import { cn } from '@/lib/utils';

import NavButton from './nav-button';
import NavMobile from './navigation-mobile';
import { ProjectSearchModal } from './project-search-modal';

export function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isScrolled, activeSection, scrollToSection } =
    useScrollNavigation(NAV_ITEMS);

  return (
    <header
      className={cn(
        'fixed top-0 right-0 left-0 z-50 transition-all duration-200',
        isScrolled
          ? 'bg-background/95 border-border border-b shadow-sm backdrop-blur-md'
          : 'bg-background/80 border-border/50 border-b backdrop-blur-md'
      )}
      role="banner"
    >
      <nav
        className="container mx-auto flex items-center justify-between px-4 py-3"
        aria-label="Main Navigation"
        itemScope
        itemType="https://schema.org/SiteNavigationElement"
      >
        {/* Logo - Home Link */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="focus-visible:ring-primary flex items-center rounded focus:outline-none focus-visible:ring-2"
          aria-label="Go to homepage"
        >
          <Logo size={50} />
        </button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:items-center md:space-x-6">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              aria-current={activeSection === item.id ? 'page' : undefined}
              className={cn(
                'hover:text-foreground relative px-3 py-2 font-medium transition duration-200 hover:scale-102 hover:cursor-pointer',
                activeSection === item.id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {item.label}
              <span
                className={cn(
                  'bg-primary absolute -bottom-1 left-0 h-[2px] rounded-full transition-all duration-200',
                  activeSection === item.id ? 'w-full' : 'w-0 group-hover:w-1/2'
                )}
              />
            </button>
          ))}

          <Button
            size="sm"
            variant="outline"
            className="border-foreground/30 hover:border-foreground/70"
            asChild
          >
            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener"
              aria-label="Download Resume"
            >
              Resume
            </a>
          </Button>

          {/* Desktop Theme + Search */}
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            <SearchIconButton onClick={() => setIsSearchOpen(true)} />
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="flex items-center justify-center space-x-3 md:hidden">
          <ThemeToggle size={32} />
          <SearchIconButton size={32} onClick={() => setIsSearchOpen(true)} />

          <NavButton
            isOpen={isMobileMenuOpen}
            size={40}
            onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />

          <NavMobile
            isOpen={isMobileMenuOpen}
            onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            scrollToSection={scrollToSection}
            activeSection={activeSection}
          />
        </div>
      </nav>

      {/* Search Modal */}
      <ProjectSearchModal open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </header>
  );
}
